import { useCallback, useEffect } from "react";
import { off, on } from "../utils/events";

/**
 * Custom React hook that adds a beforeunload event listener to the window object.
 * This hook is used to prompt the user with a confirmation dialog before leaving the page.
 *
 * @param enabled - Determines whether the beforeunload event listener should be enabled or not.
 *                  If a function is provided, it will be called to determine the enabled state.
 *                  If a boolean value is provided, it will be used directly.
 * @returns void
 */
const useBeforeUnload = (enabled: boolean | (() => boolean) = true) => {
	const handler = useCallback(
		(event: BeforeUnloadEvent) => {
			const finalEnabled = typeof enabled === "function" ? enabled() : enabled;

			if (!finalEnabled) {
				return;
			}

			event.preventDefault();
			event.returnValue = true;
		},
		[enabled],
	);

	useEffect(() => {
		if (!enabled) {
			return;
		}

		on(window, "beforeunload", handler);

		return () => off(window, "beforeunload", handler);
	}, [enabled, handler]);
};

export { useBeforeUnload };
